import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Button, IconButton, Chip, Alert, Typography, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MarketWatch from '../Components/MarketWatch';
import { Trades, QuickOrder } from '../Components/Trades';
import { OrderMonitor } from '../Components/OrdersMonitoring';
import Connector from '../Components/Connector';
import Monitoring from '../Components/Monitoring';
import './DashboardPage.css';
import { useSignalRContext } from "../SignalR/useSignalR";
import { useAuth } from "../provider/authProvider";
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ManualOrderClose from '../pages/ManualOrderClose';
import SettingsMain from '../Components/Settings/SettingsMain';
import { GetUser } from '../api/auth';
import { getAccountsByUserId } from '../api/account_api';
import { updateStrategyParameters, executeManualOrder, resetStrategySnapshot, getStrategyById, stopStrategy } from '../api/strategyApi';
import { toast } from 'react-toastify';

const DashboardPage = () => {
    const navigate = useNavigate();
    const { disconnect, clear, dataSource, isMockMode, mockDataWarning, connectionQuality, lastRealDataUpdate, strategyDashboardData, positionsList, tickData, loadRunningStrategies } = useSignalRContext();
    const { clearToken, hasPermission, isAdmin, getCurrentUser, token } = useAuth();

    // Get current user data
    const currentUser = getCurrentUser();
    const userId = currentUser?.id; // Extract userId to prevent infinite loop

    // Helper function to get strategy ID from different possible property names
    const getStrategyId = useCallback(() => {
        return strategyDashboardData?.id ||
               strategyDashboardData?.strategyId ||
               strategyDashboardData?.StrategyId ||
               strategyDashboardData?.strategyID;
    }, [strategyDashboardData]);

    // User accounts state - MUST BE DECLARED BEFORE calculateTotalPnL
    const [userAccounts, setUserAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(() => {
        // Load persisted account from localStorage on mount
        const saved = localStorage.getItem('selectedAccount');
        return saved ? JSON.parse(saved) : null;
    });

    // Calculate real-time total P&L from positions for the current strategy
    const calculateTotalPnL = useCallback(() => {
        if (!positionsList || positionsList.length === 0) {
            return 0;
        }

        // Extract accountNumber from selectedAccount
        const accountNumber = selectedAccount?.accountNumber ||
                             selectedAccount?.account_number ||
                             (typeof selectedAccount === 'number' ? selectedAccount : null);

        if (!accountNumber) {
            return 0;
        }

        // Filter positions by accountNumber instead of strategyId
        const accountPositions = positionsList.filter(pos => {
            const posAccountNumber = pos.accountNumber || pos.AccountNumber;
            return posAccountNumber && posAccountNumber.toString() === accountNumber.toString();
        });

        if (accountPositions.length === 0) {
            return 0;
        }

        let totalPnL = 0;

        accountPositions.forEach((position) => {
            const symbol = position.symbolName || position.symbol;
            const volume = parseFloat(position.volume || position.Volume);
            const openPrice = parseFloat(position.openPrice || position.entryPrice || position.OpenPrice || position.EntryPrice);
            const side = position.direction || position.tradeSide || position.Direction || position.TradeSide;

            if (!symbol || isNaN(volume) || isNaN(openPrice) || !side) {
                return;
            }

            // Get current price from tick data
            const quote = tickData?.find(tick => tick.symbol === symbol);
            if (!quote) {
                return;
            }

            const currentPrice = side === "Buy" ? parseFloat(quote.bid) : parseFloat(quote.ask);
            if (isNaN(currentPrice)) {
                return;
            }

            // Calculate profit using the same formula as Position component
            const contractSize = 100000; // Standard for forex
            const symbolDigits = symbol.includes('JPY') ? 3 : 5;

            const priceDiff = side === "Buy"
                ? (currentPrice - openPrice)
                : (openPrice - currentPrice);

            const profitValue = priceDiff * contractSize * volume;
            totalPnL += profitValue;
        });

        return totalPnL;
    }, [positionsList, tickData, selectedAccount]);

    // Calculate real-time P&L directly (calculateTotalPnL is already memoized via useCallback)
    // Removed useMemo wrapper to fix React warning: "Cannot update component while rendering"
    const realTimePnL = calculateTotalPnL();

    // Persist selected account to localStorage whenever it changes
    useEffect(() => {
        if (selectedAccount !== null) {
            localStorage.setItem('selectedAccount', JSON.stringify(selectedAccount));
        }
    }, [selectedAccount]);

    // Fetch user accounts on mount
    useEffect(() => {
        const fetchAccounts = async () => {
            if (!token || !userId) {
                console.warn('Cannot fetch accounts: Missing token or user ID');
                return;
            }

            try {
                const result = await getAccountsByUserId(token, userId);

                if (result.success && result.data && result.data.length > 0) {
                    setUserAccounts(result.data);

                    // Check if there's a saved account that exists in the user's accounts
                    const savedAccount = localStorage.getItem('selectedAccount');
                    if (savedAccount) {
                        const savedAccountData = JSON.parse(savedAccount);

                        // Handle both old format (number) and new format (object)
                        const savedAccountNumber = typeof savedAccountData === 'number'
                            ? savedAccountData
                            : savedAccountData?.accountNumber;

                        const accountExists = result.data.find(acc => acc.accountNumber === savedAccountNumber);

                        if (accountExists) {
                            // Use saved account - store full object
                            setSelectedAccount(accountExists);
                        } else {
                            // Saved account no longer exists, use first account
                            setSelectedAccount(result.data[0]);
                        }
                    } else {
                        // No saved account, select the first account by default
                        setSelectedAccount(result.data[0]);
                    }
                } else {
                    setUserAccounts([]);
                }
            } catch (error) {
                console.error('❌ Error fetching user accounts:', error);
                setUserAccounts([]);
            }
        };

        fetchAccounts();
    }, [token, userId]); // ✅ FIXED: Use userId (string) instead of currentUser (object)

    // Panel state management
    const [panelStates, setPanelStates] = useState({
        'market-watch': true,
        'monitoring': true,
        'right-panel': true,
        'bottom-panel': true
    });
    
    // Bottom panel resize state
    const [bottomPanelHeight, setBottomPanelHeight] = useState(200);
    const [isResizing, setIsResizing] = useState(false);
    
    // Right panel mode: 'quick-order' or 'strategy'
    const [currentRightPanel, setCurrentRightPanel] = useState('strategy');

    // Selected symbol state for Quick Order and Spread Order
    const [selectedSymbol, setSelectedSymbol] = useState(null);

    const [showFileDropdown, setShowFileDropdown] = useState(false);
    const [showViewDropdown, setShowViewDropdown] = useState(false);
    const [showOrderStrategySubmenu, setShowOrderStrategySubmenu] = useState(false);

    // Account menu state
    const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
    const accountMenuOpen = Boolean(accountMenuAnchor);

    // Settings modal state
    const [showSettings, setShowSettings] = useState(false);

    // Trade panel expand modal state
    const [showTradePanelModal, setShowTradePanelModal] = useState(false);

    // Full Preview modal state
    const [showFullPreviewModal, setShowFullPreviewModal] = useState(false);

    // Strategy parameters state for editable inputs
    // These values are ONLY loaded once initially and then user-editable
    // They do NOT update from SignalR after initial load
    const [strategyParams, setStrategyParams] = useState({
        buyGrid: '',
        sellGrid: '',
        buyLegTP: '',
        sellLegTP: '',
        accumulativeTP: '',
        accumulativeSL: '',
        benchmarkThresholdBuy: '',
        benchmarkThresholdSell: ''
    });

    // Track if initial values have been loaded (only load once per strategy)
    const lastLoadedStrategyIdRef = useRef(null);

    // Load running strategies on mount (after refresh or tab switch)
    useEffect(() => {
        if (loadRunningStrategies && token) {
            loadRunningStrategies();
        }
    }, [loadRunningStrategies, token]);

    // Load strategy parameters from API when strategy ID is available
    useEffect(() => {
        const fetchStrategyParameters = async () => {
            const currentStrategyId = getStrategyId();

            // Only fetch if strategy ID changed or first load
            if (!currentStrategyId) {
                return;
            }

            if (currentStrategyId === lastLoadedStrategyIdRef.current) {
                return;
            }

            if (!token) {
                return;
            }

            try {
                const strategyData = await getStrategyById(token, currentStrategyId);

                if (strategyData?.parameters) {
                    const params = strategyData.parameters;

                    // Map parameters from API to local state
                    const newParams = {
                        buyGrid: params['GridManagement.BuyGrid'] || params.BuyGrid || params.buyGrid || '',
                        sellGrid: params['GridManagement.SellGrid'] || params.SellGrid || params.sellGrid || '',
                        buyLegTP: params['GridManagement.BuyLegTP'] || params.BuyLegTP || params.buyLegTP || '',
                        sellLegTP: params['GridManagement.SellLegTP'] || params.SellLegTP || params.sellLegTP || '',
                        accumulativeTP: params['GridManagement.AccumulativeTP'] || params.AccumulativeTP || params.accumulativeTP || '',
                        accumulativeSL: params['GridManagement.AccumulativeSL'] || params.AccumulativeSL || params.accumulativeSL || '',
                        benchmarkThresholdBuy: params['GridManagement.BenchmarkThresholdBuy'] || params.BenchmarkThresholdBuy || params.benchmarkThresholdBuy || '',
                        benchmarkThresholdSell: params['GridManagement.BenchmarkThresholdSell'] || params.BenchmarkThresholdSell || params.benchmarkThresholdSell || ''
                    };

                    setStrategyParams(newParams);
                    lastLoadedStrategyIdRef.current = currentStrategyId;
                }
            } catch (error) {
                console.error('❌ Error fetching strategy parameters:', error);
            }
        };

        fetchStrategyParameters();
    }, [strategyDashboardData?.id, token, getStrategyId]); // Re-run when strategy ID changes

    // Handle strategy parameter input change - completely independent of SignalR
    // Wrapped in useCallback to prevent infinite re-renders in useMemo dependencies
    const handleParamChange = useCallback((key, value) => {
        setStrategyParams(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    // Handle Apply button click
    // Wrapped in useCallback to prevent infinite re-renders in useMemo dependencies
    const handleApplyParameters = useCallback(async () => {
        try {
            // Use strategy ID from SignalR data - try different possible field names
            const strategyId = strategyDashboardData?.id ||
                             strategyDashboardData?.strategyId ||
                             strategyDashboardData?.StrategyId ||
                             strategyDashboardData?.strategyID;

            if (!strategyId) {
                toast.error('No strategy ID found in SignalR data. Check console for details.', {
                    position: "top-right",
                    autoClose: 5000
                });
                return;
            }

            // Prepare parameters with dot notation (GridManagement.ParameterName)
            const parameters = {
                "GridManagement.BuyGrid": parseFloat(strategyParams.buyGrid) || 0,
                "GridManagement.SellGrid": parseFloat(strategyParams.sellGrid) || 0,
                "GridManagement.BuyLegTP": parseFloat(strategyParams.buyLegTP) || 0,
                "GridManagement.SellLegTP": parseFloat(strategyParams.sellLegTP) || 0,
                "GridManagement.AccumulativeTP": parseFloat(strategyParams.accumulativeTP) || 0,
                "GridManagement.AccumulativeSL": parseFloat(strategyParams.accumulativeSL) || 0,
                "GridManagement.BenchmarkThresholdBuy": parseFloat(strategyParams.benchmarkThresholdBuy) || 0,
                "GridManagement.BenchmarkThresholdSell": parseFloat(strategyParams.benchmarkThresholdSell) || 0
            };

            const result = await updateStrategyParameters(token, strategyId, parameters);

            if (result.success) {
                toast.success('Strategy parameters updated successfully', {
                    position: "top-right",
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('Error updating strategy parameters:', error);
            toast.error(error?.message || 'Failed to update strategy parameters', {
                position: "top-right",
                autoClose: 5000
            });
        }
    }, [strategyDashboardData, strategyParams, token]); // Dependencies for useCallback

    // Manual Order Handlers
    const handleBuySpread = useCallback(async () => {
        try {
            const strategyId = getStrategyId();
            const futuresSymbol = strategyDashboardData?.marketData?.futuresSymbol;
            const spotSymbol = strategyDashboardData?.marketData?.spotSymbol;
            const buyLotSize = parseFloat(strategyDashboardData?.parameters?.['GridManagement.BuyBaseLotSize']) || 0.01;

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            if (!futuresSymbol || !spotSymbol) {
                toast.error('Symbol information not available', { position: "top-right", autoClose: 2000 });
                return;
            }

            // Extract accountNumber from selectedAccount
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const orders = [
                {
                    symbolName: futuresSymbol,
                    tradeSide: "Buy",
                    quantity: buyLotSize
                },
                {
                    symbolName: spotSymbol,
                    tradeSide: "Sell",
                    quantity: buyLotSize
                }
            ];

            const result = await executeManualOrder(token, strategyId, "OpenBuySpread", accountNumber, orders);

            if (result.success) {
                toast.success('Buy Spread opened successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to open Buy Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to execute Buy Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [strategyDashboardData, token, getStrategyId, selectedAccount]);

    const handleSellSpread = useCallback(async () => {
        try {
            const strategyId = getStrategyId();
            const futuresSymbol = strategyDashboardData?.marketData?.futuresSymbol;
            const spotSymbol = strategyDashboardData?.marketData?.spotSymbol;
            const sellLotSize = parseFloat(strategyDashboardData?.parameters?.['GridManagement.SellBaseLotSize']) || 0.01;

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            if (!futuresSymbol || !spotSymbol) {
                toast.error('Symbol information not available', { position: "top-right", autoClose: 2000 });
                return;
            }

            // Extract accountNumber from selectedAccount
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const orders = [
                {
                    symbolName: futuresSymbol,
                    tradeSide: "Sell",
                    quantity: sellLotSize
                },
                {
                    symbolName: spotSymbol,
                    tradeSide: "Buy",
                    quantity: sellLotSize
                }
            ];

            const result = await executeManualOrder(token, strategyId, "OpenSellSpread", accountNumber, orders);

            if (result.success) {
                toast.success('Sell Spread opened successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to open Sell Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to execute Sell Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [strategyDashboardData, token, getStrategyId, selectedAccount]);

    const handleCloseBuy = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            // Extract accountNumber from selectedAccount
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseLastBuySpread", accountNumber);

            if (result.success) {
                toast.success('Last Buy Spread closed successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to close Buy Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to close Buy Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [strategyDashboardData, token, getStrategyId, selectedAccount]);

    const handleCloseSell = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            // Extract accountNumber from selectedAccount
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseLastSellSpread", accountNumber);

            if (result.success) {
                toast.success('Last Sell Spread closed successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to close Sell Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to close Sell Spread', { position: "top-right", autoClose: 2000 });
        }
    }, [strategyDashboardData, token, getStrategyId, selectedAccount]);

    const handleCloseAll = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            // Extract accountNumber from selectedAccount
            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseAll", accountNumber);

            if (result.success) {
                toast.success('All positions closed successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to close all positions', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to close all positions', { position: "top-right", autoClose: 2000 });
        }
    }, [strategyDashboardData, token, getStrategyId, selectedAccount]);

    // Handle Reset Strategy
    const handleResetStrategy = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await resetStrategySnapshot(token, strategyId);

            if (result.success) {
                toast.success('Strategy snapshot reset successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to reset strategy snapshot', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to reset strategy snapshot', { position: "top-right", autoClose: 2000 });
        }
    }, [token, getStrategyId]);

    // Handle symbol selection from Market Watch
    const handleSymbolSelect = (symbol) => {
        setSelectedSymbol(symbol);
    };

    // Collapse state for each panel
    const [collapsed, setCollapsed] = useState({
        'market-watch': false,
        'monitoring': false,
        'right-panel': false,
        'bottom-panel': false,
        'chart': false
    });

    // NEW: Global connection status indicator
    const getGlobalConnectionStatus = () => {
        if (dataSource === "real") {
            return {
                icon: <CheckCircleIcon />,
                color: "#22c55e",
                text: "LIVE DATA",
                bgColor: "rgba(34, 197, 94, 0.1)",
                borderColor: "#22c55e",
                dotClass: "connected"
            };
        } else if (dataSource === "mock") {
            return {
                icon: <WarningIcon />,
                color: "#f59e0b",
                text: "CACHED DATA",
                bgColor: "rgba(245, 158, 11, 0.1)",
                borderColor: "#f59e0b",
                dotClass: "cached"
            };
        } else {
            return {
                icon: <SignalWifiOffIcon />,
                color: "#ef4444",
                text: "DISCONNECTED",
                bgColor: "rgba(239, 68, 68, 0.1)",
                borderColor: "#ef4444",
                dotClass: "disconnected"
            };
        }
    };

    const connectionStatus = getGlobalConnectionStatus();

    const handleCollapse = (panelId) => {
        setCollapsed(prev => ({ ...prev, [panelId]: !prev[panelId] }));
    };

    // Panel controls
    const closePanel = (panelId) => {
        setPanelStates(prev => ({ ...prev, [panelId]: false }));
    };

    const togglePanel = (panelId) => {
        setPanelStates(prev => ({ ...prev, [panelId]: !prev[panelId] }));
    };

    const showRightPanel = (type) => {
        if (!panelStates['right-panel']) {
            setPanelStates(prev => ({ ...prev, 'right-panel': true }));
        }
        setCurrentRightPanel(type);
    };

    const handleSettingsClick = () => {
        setShowSettings(true);
    };

    // Account menu handlers
    const handleAccountMenuOpen = (event) => {
        setAccountMenuAnchor(event.currentTarget);
    };

    const handleAccountMenuClose = () => {
        setAccountMenuAnchor(null);
    };

    const handleAccountSelect = async (accountNumber) => {
        try {
            // Check if there's a running strategy
            const currentStrategyId = getStrategyId();

            if (currentStrategyId) {
                // Stop the running strategy
                const result = await stopStrategy(token, currentStrategyId);

                if (result.success) {
                    toast.success('Strategy stopped automatically on account switch', {
                        position: "top-right",
                        autoClose: 2000
                    });
                } else {
                    toast.warning('Could not stop strategy automatically', {
                        position: "top-right",
                        autoClose: 2000
                    });
                }
            }

            // Switch to the new account - store full account object
            const account = userAccounts.find(acc => acc.accountNumber === accountNumber);
            if (account) {
                setSelectedAccount(account);
            }
            handleAccountMenuClose();
        } catch (error) {
            console.error('Error switching account:', error);
            toast.error('Error switching account', {
                position: "top-right",
                autoClose: 2000
            });
        }
    };

    const handleTradePanelExpand = () => {
        setShowTradePanelModal(true);
    };

    // Helper function to get decimal precision based on symbol
    const getDecimalPrecision = useCallback((symbol) => {
        if (!symbol) return 5; // Default to 5 for forex

        const symbolUpper = symbol.toUpperCase();

        // Remove common suffixes to get clean symbol (e.g., GBPUSD_ct → GBPUSD)
        const symbolClean = symbolUpper.replace(/_CT|_SPOT|_FUT|_FUTURES/g, '');

        // === TIER 1: Metals (2 decimals) ===
        if (symbolClean.includes('XAU') || symbolClean.includes('GOLD') ||
            symbolClean.includes('XAG') || symbolClean.includes('SILVER') ||
            symbolClean.includes('XPT') || symbolClean.includes('XPD')) {
            return 2;
        }

        // === TIER 2: JPY Pairs (3 decimals) ===
        if (symbolClean.includes('JPY')) {
            return 3;
        }

        // === TIER 3: Crypto (varies) ===
        if (symbolClean.includes('BTC')) return 2;
        if (symbolClean.includes('ETH')) return 4;

        // === TIER 4: Forex Pairs (5 decimals) ===
        // Clean 6-character forex pattern (EURUSD, GBPUSD, etc.)
        if (symbolClean.length === 6 && /^[A-Z]{6}$/.test(symbolClean)) {
            return 5;
        }

        // Common forex pairs (even with noise) - fallback detection
        const forexCurrencies = ['EUR', 'GBP', 'AUD', 'NZD', 'USD', 'CHF', 'CAD'];
        const hasForexCurrency = forexCurrencies.some(curr => symbolClean.includes(curr));
        if (hasForexCurrency && symbolClean.length >= 6) {
            return 5;
        }

        // === DEFAULT: Assume forex (most common case) ===
        return 5;
    }, []);

    // Helper function to format number with proper decimals
    const formatWithPrecision = useCallback((value, symbol) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        const precision = getDecimalPrecision(symbol);
        return Number(value).toFixed(precision);
    }, [getDecimalPrecision]);

    // Layout for react-grid-layout
    const layout = [
        { i: 'market-watch', x: 0, y: 0, w: 2, h: 8, minW: 2, minH: 4, maxW: 3 },
        { i: 'chart', x: 2, y: 0, w: 6, h: 8, minW: 4, minH: 6 },
        { i: 'right-panel', x: 8, y: 0, w: 2, h: 8, minW: 2, minH: 4, maxW: 3 },
        { i: 'bottom-panel', x: 0, y: 8, w: 10, h: 4, minH: 3 }
    ];

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container') && 
                !event.target.closest('.trading-tabs-container') && 
                !event.target.closest('.header-tabs') &&
                !event.target.closest('.MuiButton-root')) {
                setShowFileDropdown(false);
                setShowViewDropdown(false);
                setShowOrderStrategySubmenu(false);
            }
        };

        // Global function to close all dropdowns
        window.closeAllDropdowns = () => {
            setShowFileDropdown(false);
            setShowViewDropdown(false);
            setShowOrderStrategySubmenu(false);
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            delete window.closeAllDropdowns;
        };
    }, []);

    // Memoize market data, performance, and position to prevent unnecessary re-renders
    const memoizedMarketData = useMemo(() => strategyDashboardData?.marketData, [strategyDashboardData?.marketData]);
    const memoizedPerformance = useMemo(() => strategyDashboardData?.performance, [strategyDashboardData?.performance]);
    const memoizedPosition = useMemo(() => strategyDashboardData?.position, [strategyDashboardData?.position]);

    // Strategy Screen Component - memoized to prevent re-renders that kill input focus
    const StrategyScreen = useMemo(() => {
        const data = strategyDashboardData;
        const marketData = memoizedMarketData;
        const performance = memoizedPerformance;
        const position = memoizedPosition;

        // Get the primary symbol for decimal precision (use futures symbol as primary)
        const primarySymbol = marketData?.futuresSymbol || marketData?.spotSymbol || '';

        return (
            <div className="strategy-container-2col">
                {/* Left Column - Controls */}
                <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Spread Monitor */}
                    <div className="spread-display">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', marginBottom: '3px' }}>
                                SPREAD MONITOR
                            </div>
                            <div style={{ marginBottom: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '10px' }}>
                                    Raw Spread:
                                </span>
                                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', fontWeight: 'bold', fontFamily: 'Courier New, monospace' }}>
                                    {formatWithPrecision(marketData?.rawSpread, primarySymbol)}
                                </span>
                            </div>
                            <div style={{ borderTop: '1px solid rgba(107, 114, 128, 0.2)', paddingTop: '3px', marginBottom: '3px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '2px' }}>
                                    <span style={{ color: '#06b6d4', fontSize: '10px' }}>🔷</span>
                                    <span style={{ color: '#06b6d4', fontSize: '10px', fontWeight: '600' }}>Beta Spread</span>
                                </div>
                                <div style={{ color: '#06b6d4', fontSize: '16px', fontWeight: 'bold', fontFamily: 'Courier New, monospace' }}>
                                    {formatWithPrecision(marketData?.betaSpread, primarySymbol)}
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid rgba(107, 114, 128, 0.2)', paddingTop: '3px', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '8px', marginBottom: '1px' }}>
                                        BETA HIGH
                                    </div>
                                    <div style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(marketData?.upperBand, primarySymbol)}
                                    </div>
                                </div>
                                <div style={{ color: 'rgba(107, 114, 128, 0.5)', fontSize: '12px' }}>|</div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '8px', marginBottom: '1px' }}>
                                        BETA LOW
                                    </div>
                                    <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: '600', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(marketData?.lowerBand, primarySymbol)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons - 2 per row, Reset Strategy full width */}
                    <div className="strategy-buttons" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <button
                            type="button"
                            className="strategy-btn buy-spread"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleBuySpread();
                            }}
                            disabled={!getStrategyId()}
                        >
                            BUY SPREAD
                        </button>
                        <button
                            type="button"
                            className="strategy-btn sell-spread"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSellSpread();
                            }}
                            disabled={!getStrategyId()}
                        >
                            SELL SPREAD
                        </button>
                        <button
                            type="button"
                            className="strategy-btn close-buy"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCloseBuy();
                            }}
                            disabled={!getStrategyId()}
                        >
                            CLOSE BUY
                        </button>
                        <button
                            type="button"
                            className="strategy-btn close-sell"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCloseSell();
                            }}
                            disabled={!getStrategyId()}
                        >
                            CLOSE SELL
                        </button>
                        <button
                            type="button"
                            className="strategy-btn close-all"
                            style={{ gridColumn: '1 / -1' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCloseAll();
                            }}
                            disabled={!getStrategyId()}
                        >
                            CLOSE ALL
                        </button>
                        <button
                            type="button"
                            className="strategy-btn reset-strategy"
                            style={{ gridColumn: '1 / -1' }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleResetStrategy();
                            }}
                            disabled={!getStrategyId()}
                        >
                            RESET STRATEGY
                        </button>
                    </div>

                    {/* Strategy Parameters Grid - All in one column */}
                    <div className="strategy-inputs-grid">
                        {[
                            { label: 'Benchmark Threshold Buy', key: 'benchmarkThresholdBuy' },
                            { label: 'Benchmark Threshold Sell', key: 'benchmarkThresholdSell' },
                            { label: 'Buy Grid', key: 'buyGrid' },
                            { label: 'Sell Grid', key: 'sellGrid' },
                            { label: 'Buy Leg TP', key: 'buyLegTP' },
                            { label: 'Sell Leg TP', key: 'sellLegTP' },
                            { label: 'Accumulative TP', key: 'accumulativeTP' },
                            { label: 'Accumulative SL', key: 'accumulativeSL' }
                        ].map((field, index) => (
                            <div key={index} className="strategy-input-group">
                                <label className="strategy-label">{field.label}</label>
                                <input
                                    type="number"
                                    step="0.001"
                                    value={strategyParams[field.key] || ''}
                                    onChange={(e) => handleParamChange(field.key, e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    className="strategy-input"
                                    style={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', cursor: 'text' }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Apply Button */}
                    <button className="strategy-apply-btn" onClick={handleApplyParameters}>Apply</button>
                </div>

            {/* Right Column - Strategy Info */}
            <div style={{ padding: '8px' }}>
                <div className="strategy-info-section">
                    {/* Two separate columns */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {/* Market Data - Left Column */}
                        <div style={{ background: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '6px', padding: '10px' }}>
                            {/* Header with icon */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(107, 114, 128, 0.3)' }}>
                                <span style={{ fontSize: '16px' }}>📊</span>
                                <span style={{ color: 'rgba(156, 163, 175, 1)', fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px' }}>MARKET DATA</span>
                            </div>

                            {/* Futures Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Futures:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{marketData?.futuresSymbol || '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Spot:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{marketData?.spotSymbol || '-'}</span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Bid/Ask Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Futures Bid:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.futuresBid, marketData?.futuresSymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Futures Ask:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.futuresAsk, marketData?.futuresSymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Spot Bid:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.spotBid, marketData?.spotSymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Spot Ask:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.spotAsk, marketData?.spotSymbol)}</span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Spread Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Raw Spread:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.rawSpread, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Beta Spread:</span>
                                    <span style={{ color: '#06b6d4', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px #06b6d4, 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.betaSpread, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Upper Band:</span>
                                    <span style={{ color: '#10b981', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px #10b981, 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.upperBand, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Lower Band:</span>
                                    <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px #ef4444, 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.lowerBand, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Median:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.medianSpread, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Buy Benchmark:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.buySpreadBenchmark, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Sell Benchmark:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.sellSpreadBenchmark, primarySymbol)}</span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Status Section */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Indicator:</span>
                                    <span style={{ color: marketData?.indicatorReady ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>{marketData?.indicatorReady ? '✓' : '✗'}</span> {marketData?.indicatorReady ? 'Ready' : 'Not Ready'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Updated:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {marketData?.lastUpdate ? new Date(marketData.lastUpdate).toLocaleTimeString() : '-'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Performance - Right Column */}
                        <div style={{ background: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '6px', padding: '10px' }}>
                            {/* Header with icon */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(107, 114, 128, 0.3)' }}>
                                <span style={{ fontSize: '16px' }}>💰</span>
                                <span style={{ color: 'rgba(156, 163, 175, 1)', fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px' }}>PERFORMANCE</span>
                            </div>

                            {/* P&L Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Total P&L:</span>
                                    <span style={{ color: realTimePnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {realTimePnL !== undefined ? `${realTimePnL >= 0 ? '+$' : '-$'}${Math.abs(realTimePnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Realized:</span>
                                    <span style={{ color: performance?.realizedPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.realizedPnL !== undefined ? `${performance.realizedPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.realizedPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Unrealized:</span>
                                    <span style={{ color: performance?.unrealizedPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.unrealizedPnL !== undefined ? `${performance.unrealizedPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.unrealizedPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Spread PnL Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>LastBuySpreadLegPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.lastBuySpreadLegPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.lastBuySpreadLegPnL !== undefined ? `${performance.pnLBreakdown.lastBuySpreadLegPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.lastBuySpreadLegPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>LastSellSpreadLegPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.lastSellSpreadLegPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.lastSellSpreadLegPnL !== undefined ? `${performance.pnLBreakdown.lastSellSpreadLegPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.lastSellSpreadLegPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>BuySpreadTotalPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.buySpreadTotalPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.buySpreadTotalPnL !== undefined ? `${performance.pnLBreakdown.buySpreadTotalPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.buySpreadTotalPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>SellSpreadTotalPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.sellSpreadTotalPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.sellSpreadTotalPnL !== undefined ? `${performance.pnLBreakdown.sellSpreadTotalPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.sellSpreadTotalPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>netPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.netPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.netPnL !== undefined ? `${performance.pnLBreakdown.netPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.netPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Spread Volume Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Buy Spread Vol:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.buySpreadLotSize?.toFixed(2) ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Sell Spread Vol:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.sellSpreadLotSize?.toFixed(2) ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Net Spread Vol:</span>
                                    <span style={{
                                        color: position?.netExposure > 0 ? '#10b981' : position?.netExposure < 0 ? '#ef4444' : 'rgba(255, 255, 255, 0.9)',
                                        fontSize: '12px',
                                        fontFamily: 'Courier New, monospace',
                                        fontWeight: 'bold',
                                        textShadow: '0 0 1px currentColor, 0.5px 0 0 currentColor'
                                    }}>
                                        {position?.netExposure?.toFixed(2) ?? '-'}
                                    </span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Active Legs Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Active Legs:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.activeLegs ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Buy Legs:</span>
                                    <span style={{ color: '#10b981', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>{position?.buySpreadLegs ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Sell Legs:</span>
                                    <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>{position?.sellSpreadLegs ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Total Lot:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.totalLotSize?.toFixed(2) || '-'}</span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Buy Entries Section */}
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                                    <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>BUY ENTRIES</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>First Buy@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.buySpreadFirstLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Last Buy@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.buySpreadLastLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        Next Buy@ <span style={{ fontSize: '12px' }}>⬇️</span>:
                                    </span>
                                    <span style={{ color: '#06b6d4', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {formatWithPrecision(position?.buySpreadNextLegEntry, primarySymbol)}
                                    </span>
                                </div>
                            </div>

                            {/* Separator Line */}
                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            {/* Sell Entries Section */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></span>
                                    <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>SELL ENTRIES</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>First Sell@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.sellSpreadFirstLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Last Sell@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.sellSpreadLastLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        Next Sell@ <span style={{ fontSize: '12px' }}>⬆️</span>:
                                    </span>
                                    <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {formatWithPrecision(position?.sellSpreadNextLegEntry, primarySymbol)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Performance Chart - Compact at bottom */}
                    <div style={{ background: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '4px', padding: '6px', marginTop: '8px' }}>
                        <div style={{ color: '#06b6d4', fontSize: '9px', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>
                            BETA SPREAD CHART
                        </div>
                        <svg width="100%" height="50" viewBox="0 0 300 50" style={{ background: 'rgba(0, 0, 0, 0.2)', borderRadius: '3px' }}>
                            {/* Grid lines */}
                            {[...Array(3)].map((_, i) => (
                                <line key={`h-${i}`} x1={15} x2={285} y1={5 + i * 13} y2={5 + i * 13} stroke="rgba(107, 114, 128, 0.15)" strokeWidth={0.5} />
                            ))}

                            {/* Equity curve line */}
                            <path
                                d="M 15,30 L 45,27 L 75,28 L 105,24 L 135,22 L 165,25 L 195,20 L 225,18 L 255,16 L 285,14"
                                stroke="#10b981"
                                strokeWidth={1.2}
                                fill="none"
                            />

                            {/* Area fill */}
                            <path
                                d="M 15,30 L 45,27 L 75,28 L 105,24 L 135,22 L 165,25 L 195,20 L 225,18 L 255,16 L 285,14 L 285,39 L 15,39 Z"
                                fill="url(#equityGradientSmall)"
                                opacity={0.3}
                            />

                            {/* Gradient definition */}
                            <defs>
                                <linearGradient id="equityGradientSmall" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.8 }} />
                                    <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0 }} />
                                </linearGradient>
                            </defs>

                            {/* Current value indicator */}
                            <circle cx="285" cy="14" r="1.5" fill="#10b981" />
                        </svg>

                        {/* Compact Stats */}
                        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '2px', fontSize: '6px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#9ca3af' }}>Start: <span style={{ color: '#fff', fontWeight: '600' }}>$5k</span></div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#9ca3af' }}>Current: <span style={{ color: '#10b981', fontWeight: '600' }}>$12.4k</span></div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#9ca3af' }}>Return: <span style={{ color: '#10b981', fontWeight: '600' }}>+149%</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        );
    }, [memoizedMarketData, memoizedPerformance, memoizedPosition, strategyParams, handleParamChange, handleApplyParameters, handleBuySpread, handleSellSpread, handleCloseBuy, handleCloseSell, handleCloseAll, handleResetStrategy, formatWithPrecision]); // Only re-render when these dependencies change

    const logout = () => {
        // Clear saved account from localStorage
        localStorage.removeItem('selectedAccount');

        disconnect();
        clearToken();
        clear();
        navigate("/", { replace: true });
    };

    // Resize functionality
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isResizing) return;
        
        const dashboardContainer = document.querySelector('.dashboard-container');
        const rect = dashboardContainer.getBoundingClientRect();
        const newHeight = rect.bottom - e.clientY;
        
        // Constrain height between 150px and 600px
        const constrainedHeight = Math.max(150, Math.min(600, newHeight));
        setBottomPanelHeight(constrainedHeight);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    // Add mouse event listeners for resize
    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const [manualCloseOpen, setManualCloseOpen] = useState(false);
    const submenuHideTimeout = useRef();

    return (
        <div className="dashboard-container">
            {/* Redesigned Header */}
            <header className="dashboard-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', background: '#181f2c', minHeight: 38, height: 58, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)' }}>
                {/* Left: Logo and Brand */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: '100%' }}>
                    {/* <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #00cfff 0%, #00e6e6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 15, color: '#fff', boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)' }}>Q</div> */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
                        <span className="brand-title-row" style={{ fontWeight: 900, fontSize: 34, color: '#3deaff', letterSpacing: 0.5, lineHeight: 1, display: 'block' }}>
                            Q<span style={{ fontWeight: 700, fontSize: 35, color: '#3deaff', marginLeft: 0 }}>TradeView</span>
                        </span>
                        <span style={{ fontSize: 11, color: '#bfc9db', fontWeight: 700,fontStyle:'italic', textTransform:'uppercase',display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>
                            High-Frequency Trading Engine
                            <span style={{ color: '#FFD600', fontSize: 9, marginLeft: 2 }}>★</span>
                        </span>
                    </div>
                </div>
                {/* Right: User Info, Actions and Connection Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Admin Panel Button - Only visible to admins */}
                    {isAdmin() && (
                        <Button 
                            size="small"
                            variant="contained" 
                            onClick={() => navigate('/adminPanel')}
                            sx={{ 
                                backgroundColor: '#7c3aed',
                                color: 'white',
                                borderRadius: 1.5, 
                                px: 2, 
                                py: 0, 
                                fontWeight: 600, 
                                fontSize: 11, 
                                minHeight: 24, 
                                height: 24, 
                                textTransform: 'none', 
                                boxShadow: 'none', 
                                minWidth: 0, 
                                padding: '4px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#6d28d9'
                                }
                            }}
                            title="Access Admin Panel"
                        >
                            👑 Admin Panel
                        </Button>
                    )}
                    
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            color: connectionStatus.dotClass === 'connected' ? '#00ff88' : connectionStatus.dotClass === 'connecting' ? '#ffc107' : '#ff5722',
                            borderColor: connectionStatus.dotClass === 'connected' ? '#00ff88' : connectionStatus.dotClass === 'connecting' ? '#ffc107' : '#ff5722',
                            borderRadius: 1.5,
                            px: 0,
                            py: 0,
                            fontWeight: 500,
                            fontSize: 11,
                            minHeight: 24,
                            height: 24,
                            textTransform: 'none',
                            background: connectionStatus.dotClass === 'connected' ? 'rgba(0,255,136,0.04)' : connectionStatus.dotClass === 'connecting' ? 'rgba(255,193,7,0.04)' : 'rgba(255,87,34,0.04)',
                            boxShadow: 'none',
                            minWidth: 0,
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <div className={`status-dot ${connectionStatus.dotClass}`} style={{ width: 4, height: 4, borderRadius: '50%', background: connectionStatus.dotClass === 'connected' ? '#00ff88' : connectionStatus.dotClass === 'connecting' ? '#ffc107' : '#ff5722', boxShadow: connectionStatus.dotClass === 'connected' ? '0 0 3px rgba(0,255,136,0.8)' : connectionStatus.dotClass === 'connecting' ? '0 0 3px rgba(255,193,7,0.8)' : '0 0 3px rgba(255,87,34,0.8)' }}></div>
                        {connectionStatus.dotClass === 'connected' ? 'CONNECTED' : connectionStatus.dotClass === 'connecting' ? 'CONNECTING' : 'DISCONNECTED'}
                    </Button>

                    {/* Account Selector Dropdown */}
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAccountMenuOpen}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
                        startIcon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
                        sx={{
                            color: '#06b6d4',
                            borderColor: '#06b6d4',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 600,
                            fontSize: 11,
                            minHeight: 24,
                            height: 24,
                            textTransform: 'none',
                            background: 'rgba(6, 182, 212, 0.08)',
                            boxShadow: 'none',
                            '&:hover': {
                                background: 'rgba(6, 182, 212, 0.15)',
                                borderColor: '#0891b2'
                            }
                        }}
                    >
                        {selectedAccount ? `A/C: ${selectedAccount.accountNumber || selectedAccount}` : 'Select Account'}
                    </Button>

                    <Menu
                        anchorEl={accountMenuAnchor}
                        open={accountMenuOpen}
                        onClose={handleAccountMenuClose}
                        PaperProps={{
                            sx: {
                                background: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: 2,
                                mt: 0.5,
                                minWidth: 200,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                            }
                        }}
                    >
                        {userAccounts.length === 0 ? (
                            <MenuItem disabled sx={{ color: '#94a3b8', fontSize: 12 }}>
                                No accounts available
                            </MenuItem>
                        ) : (
                            userAccounts.map((account) => (
                                <MenuItem
                                    key={account.accountNumber}
                                    onClick={() => handleAccountSelect(account.accountNumber)}
                                    selected={selectedAccount?.accountNumber === account.accountNumber || selectedAccount === account.accountNumber}
                                    sx={{
                                        color: '#e2e8f0',
                                        fontSize: 12,
                                        py: 1.5,
                                        '&:hover': {
                                            background: 'rgba(6, 182, 212, 0.15)'
                                        },
                                        '&.Mui-selected': {
                                            background: 'rgba(6, 182, 212, 0.2)',
                                            '&:hover': {
                                                background: 'rgba(6, 182, 212, 0.25)'
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#06b6d4' }}>
                                                {account.accountNumber}
                                            </Typography>
                                            <Chip
                                                label={account.accountType === 0 ? 'DEMO' : 'LIVE'}
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    background: account.accountType === 0 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                                                    color: account.accountType === 0 ? '#f59e0b' : '#22c55e',
                                                    border: `1px solid ${account.accountType === 0 ? '#f59e0b' : '#22c55e'}`
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
                                            Balance: ${account.balance?.toFixed(2) || '0.00'} {account.currency || 'USD'}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Menu>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', marginRight: 6, height: 24 }}>
                        <span style={{ fontWeight: 700, color: '#fff', fontSize: 12, lineHeight: 1 }}>
                            {currentUser?.firstName && currentUser?.lastName 
                                ? `${currentUser.firstName} ${currentUser.lastName}` 
                                : currentUser?.email || 'User'
                            }
                        </span>
                        <span style={{ fontSize: 9, color: '#bfc9db', fontWeight: 600, lineHeight: 1 }}>
                            {currentUser?.role === 'Admin' ? 'ADMIN ACCOUNT' : 'PRO ACCOUNT'}
                        </span>
                    </div>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #7b61ff 0%, #a890fe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#fff', marginRight: 6 }}>
                        {currentUser?.firstName && currentUser?.lastName 
                            ? `${currentUser.firstName.charAt(0)}${currentUser.lastName.charAt(0)}`.toUpperCase()
                            : currentUser?.email 
                                ? currentUser.email.charAt(0).toUpperCase()
                                : 'U'
                        }
                    </div>
                    <Button variant="outlined" sx={{ color: '#1ca7c7', borderColor: '#1ca7c7', borderRadius: 1.5, px: 0, py: 0, fontWeight: 500, fontSize: 11, minHeight: 24, height: 24, textTransform: 'none', background: 'rgba(0,207,255,0.04)', boxShadow: 'none', minWidth: 0, padding: '4px 8px' }}>Help</Button>
                </div>
            </header>

            {/* Toolbar */}
            <div className="dashboard-toolbar">
                <div className="toolbar-left">
                    {/* File Dropdown */}
                    <div className="dropdown-container">
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowFileDropdown(!showFileDropdown);
                                setShowViewDropdown(false);
                                setShowOrderStrategySubmenu(false);
                            }}
                            className="toolbar-button"
                        >
                            File
                        </button>
                        <div className={`dropdown-menu ${showFileDropdown ? 'show' : ''}`}>
                            <div className="dropdown-item">📄 New Chart</div>
                            <div className="dropdown-item">📁 Open Data Folder</div>
                            <div className="dropdown-item">💾 Save Template</div>
                            <div className="dropdown-item">   Print Chart</div>
                            <div className="dropdown-item" onClick={logout} style={{color: '#ef4444', fontWeight: 600, borderTop: '1px solid rgba(6,182,212,0.1)'}}>🚪 Logout</div>
                        </div>
                    </div>

                    {/* View Dropdown */}
                    <div className="dropdown-container">
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowViewDropdown(!showViewDropdown);
                                setShowFileDropdown(false);
                                setShowOrderStrategySubmenu(false);
                            }}
                            className="toolbar-button"
                        >
                            View
                        </button>
                        <div className={`dropdown-menu ${showViewDropdown ? 'show' : ''}`}>
                            {(hasPermission('Dashboard.MarketWatchSection') || isAdmin()) && (
                            <div 
                                className="dropdown-item" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePanel('market-watch');
                                    setShowViewDropdown(false);
                                }}
                            >
                                <span>📊 Market Watch</span>
                                {panelStates['market-watch'] && <span className="checkmark">✓</span>}
                            </div>
                            )}
                            
                            {(hasPermission('Dashboard.MonitoringSection') || isAdmin()) && (
                            <div 
                                className="dropdown-item" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePanel('monitoring');
                                    setShowViewDropdown(false);
                                }}
                            >
                                <span>⚡ Monitoring</span>
                                {panelStates['monitoring'] && <span className="checkmark">✓</span>}
                            </div>
                            )}
                            
                            {/* Order/Strategy Submenu */}
                            <div 
                                className="dropdown-item submenu-item"
                                onMouseEnter={() => {
                                    if (submenuHideTimeout.current) clearTimeout(submenuHideTimeout.current);
                                    setShowOrderStrategySubmenu(true);
                                }}
                                onMouseLeave={() => {
                                    submenuHideTimeout.current = setTimeout(() => setShowOrderStrategySubmenu(false), 200);
                                }}
                            >
                                <span>📋 Order/Strategy</span>
                                <span className="submenu-arrow">▶</span>
                                
                                {/* Submenu */}
                                <div 
                                    className={`dropdown-submenu ${showOrderStrategySubmenu ? 'show' : ''}`}
                                    onMouseEnter={() => {
                                        if (submenuHideTimeout.current) clearTimeout(submenuHideTimeout.current);
                                        setShowOrderStrategySubmenu(true);
                                    }}
                                    onMouseLeave={() => {
                                        submenuHideTimeout.current = setTimeout(() => setShowOrderStrategySubmenu(false), 200);
                                    }}
                                >
                                    <div
                                        className="dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showRightPanel('strategy');
                                            setShowViewDropdown(false);
                                            setShowOrderStrategySubmenu(false);
                                        }}
                                    >
                                        <span>ArbX TRADER - GOLDARB</span>
                                        {panelStates['right-panel'] && currentRightPanel === 'strategy' && <span className="checkmark">✓</span>}
                                    </div>
                                    {(hasPermission('Dashboard.QuickOrder') || isAdmin()) && (
                                    <div
                                        className="dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showRightPanel('quick-order');
                                            setShowViewDropdown(false);
                                            setShowOrderStrategySubmenu(false);
                                        }}
                                    >
                                        <span>Quick Order</span>
                                        {panelStates['right-panel'] && currentRightPanel === 'quick-order' && <span className="checkmark">✓</span>}
                                    </div>
                                    )}
                                    {(hasPermission('Dashboard.SpreadOrder') || isAdmin()) && (
                                    <div
                                        className="dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showRightPanel('spread-order');
                                            setShowViewDropdown(false);
                                            setShowOrderStrategySubmenu(false);
                                        }}
                                    >
                                        <span>Spread Order</span>
                                        {panelStates['right-panel'] && currentRightPanel === 'spread-order' && <span className="checkmark">✓</span>}
                                    </div>
                                    )}
                                </div>
                            </div>
                            
                            {(hasPermission('Dashboard.TradePanel') || isAdmin()) && (
                            <div 
                                className="dropdown-item" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePanel('bottom-panel');
                                    setShowViewDropdown(false);
                                }}
                            >
                                <span>📈 Trade Panel</span>
                                {panelStates['bottom-panel'] && <span className="checkmark">✓</span>}
                            </div>
                            )}
                        </div>
                    </div>

                    <button className="toolbar-button">Insert</button>
                    <button className="toolbar-button">Charts</button>
                    <button className="toolbar-button">Tools</button>
                    <button className="toolbar-button">Window</button>
                </div>

                <div className="toolbar-icons">
                    <button className="icon-button">🔍</button>
                    <button className="icon-button">📊</button>
                    <button className="icon-button">📈</button>
                    {(() => {
                        const hasSettingsPermission = hasPermission('Settings') || isAdmin();
                        return hasSettingsPermission;
                    })() && (
                    <button className="icon-button" onClick={handleSettingsClick}><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></button>
                    )}
                    
                    
                </div>
            </div>


            {/* Modular Grid Layout */}
            <div className={`main-content${panelStates['bottom-panel'] ? ' has-bottom-panel' : ''}`}>
                {panelStates['market-watch'] && (hasPermission('Dashboard.MarketWatchSection') || isAdmin()) && (
                    <div className="panel market-watch-panel resizable-panel">
                        <div className="panel-header slim-header">
                            <span>📊 Market Watch</span>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('market-watch')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small"><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('market-watch')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['market-watch'] && (
                        <div className="panel-content">
                            <Box sx={{
                                '& .MuiTableContainer-root': {
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    marginTop: 0
                                },
                                '& .MuiPaper-root': {
                                    backgroundColor: 'transparent',
                                    color: 'white'
                                },
                                '& .MuiTableCell-root': {
                                    color: 'white',
                                    borderBottom: '1px solid rgba(6, 182, 212, 0.1)',
                                    fontSize: '0.75rem'
                                },
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                    fontWeight: 'bold'
                                },
                                '& .MuiTableRow-root:hover': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)'
                                }
                            }}>
                                <MarketWatch onSymbolSelect={handleSymbolSelect} />
                            </Box>
                        </div>
                        )}
                    </div>
                )}
                
                {panelStates['monitoring'] && (hasPermission('Dashboard.MonitoringSection') || isAdmin()) && (
                    <div className="panel monitoring-panel resizable-panel">
                        <div className="panel-header slim-header">
                            <span>⚡ Monitoring</span>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('monitoring')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small"><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('monitoring')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['monitoring'] && (
                            <div className="panel-content">
                                <Monitoring />
                            </div>
                        )}
                    </div>
                )}

                {panelStates['right-panel'] && ((hasPermission('Dashboard.QuickOrder') || hasPermission('Dashboard.SpreadOrder')) || isAdmin()) && (
                    <div className="panel right-panel resizable-panel">
                        <div className="panel-header slim-header">
                            <span>
                                {currentRightPanel === 'quick-order' && '📝 Quick Order'}
                                {currentRightPanel === 'spread-order' && '📋 Spread Order'}
                                {currentRightPanel === 'strategy' && '📊 ArbX TRADER - GOLDARB'}
                            </span>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('right-panel')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small"><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('right-panel')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['right-panel'] && (
                        <div className="panel-content">
                            {currentRightPanel === 'quick-order' && (hasPermission('Dashboard.QuickOrder') || isAdmin()) && <QuickOrder selectedSymbol={selectedSymbol} selectedAccount={selectedAccount} />}
                            {currentRightPanel === 'spread-order' && (hasPermission('Dashboard.SpreadOrder') || isAdmin()) && <Trades selectedSymbol={selectedSymbol} selectedAccount={selectedAccount} />}
                            {currentRightPanel === 'strategy' && StrategyScreen}
                        </div>
                        )}
                    </div>
                )}
                {panelStates['bottom-panel'] && (hasPermission('Dashboard.TradePanel') || isAdmin()) && (
                    <div className="panel bottom-panel resizable-panel" style={{ height: `${bottomPanelHeight}px` }}>
                        <div 
                            className="resize-handle-horizontal"
                            onMouseDown={handleMouseDown}
                            style={{
                                position: 'absolute',
                                top: '-4px',
                                left: '0',
                                right: '0',
                                height: '8px',
                                cursor: 'ns-resize',
                                backgroundColor: 'transparent',
                                zIndex: 10,
                                borderTop: isResizing ? '2px solid #06b6d4' : '1px solid transparent'
                            }}
                        />
                        <div className="panel-header slim-header with-tabs">
                            <div className="header-tabs">
                                <Box 
                                    className="trading-tabs-container"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <OrderMonitor tabsOnly={true} selectedAccount={selectedAccount} />
                                </Box>
                            </div>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('bottom-panel')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small" onClick={handleTradePanelExpand}><FullscreenIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('bottom-panel')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['bottom-panel'] && (
                        <div className="panel-content">
                            <Box sx={{
                                backgroundColor: 'transparent',
                                color: 'white',
                                '& .MuiTableContainer-root': {
                                    backgroundColor: 'transparent'
                                },
                                '& .MuiTable-root': {
                                    '& .MuiTableCell-root': {
                                        color: 'white',
                                        borderBottom: '1px solid rgba(6, 182, 212, 0.1)',
                                        fontSize: '0.75rem'
                                    },
                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                        backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                        fontWeight: 'bold'
                                    }
                                }
                            }}>
                                <OrderMonitor contentOnly={true} selectedAccount={selectedAccount} />
                            </Box>
                        </div>
                        )}
                    </div>
                )}
            </div>
            <div className="status-bar">
                <span style={{marginRight:24}}>Balance: $100,000.00</span>
                <span style={{marginRight:24}}>Equity: $99,800.00</span>
                <span style={{marginRight:24}}>Margin: $1,200.00</span>
                <span style={{marginRight:24}}>Free Margin: $98,600.00</span>
                <span>Margin Level: 8300%</span>
            </div>
            <ManualOrderClose open={manualCloseOpen} onClose={() => setManualCloseOpen(false)} />
            <SettingsMain open={showSettings} onClose={() => setShowSettings(false)} selectedAccount={selectedAccount} />
            
            {/* Trade Panel Expand Modal */}
            {showTradePanelModal && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setShowTradePanelModal(false)}
                >
                    <Box
                        sx={{
                            width: '90%',
                            height: '90%',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '8px',
                            border: '1px solid rgba(6, 182, 212, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            position: 'relative',
                            zIndex: 10000
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <Box sx={{
                            padding: '8px 12px',
                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                            backgroundColor: '#0a0e1a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: '36px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <Typography sx={{ color: '#e2f3f4', fontWeight: 600, fontSize: '14px' }}>
                                Trade Panel - Expanded View
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setShowTradePanelModal(false)}
                                sx={{ color: '#e2f3f4' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Modal Content */}
                        <Box sx={{
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <OrderMonitor selectedAccount={selectedAccount} />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Full Preview Modal */}
            {showFullPreviewModal && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setShowFullPreviewModal(false)}
                >
                    <Box
                        sx={{
                            width: '80%',
                            maxWidth: '900px',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '8px',
                            border: '1px solid rgba(6, 182, 212, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            position: 'relative',
                            zIndex: 10000,
                            maxHeight: '90vh'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <Box sx={{
                            padding: '12px 16px',
                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                            backgroundColor: '#0a0e1a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{ color: '#e2f3f4', fontWeight: 600, fontSize: '16px' }}>
                                Strategy Full Preview
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setShowFullPreviewModal(false)}
                                sx={{ color: '#e2f3f4' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Modal Content */}
                        <Box sx={{
                            flex: 1,
                            overflow: 'auto',
                            padding: '20px',
                            backgroundColor: '#1a1a1a',
                            color: 'white'
                        }}>
                            {/* Main Layout: 2 Columns - Chart (40%), Info (60%) */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '40% 60%',
                                gap: 2
                            }}>
                                {/* COLUMN 1 - Beta Spread Chart */}
                            <Box sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                borderRadius: '8px',
                                padding: '16px'
                            }}>
                                <Typography sx={{
                                    color: '#06b6d4',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    mb: 2
                                }}>
                                    📊 BETA SPREAD CHART
                                </Typography>

                                {/* Chart Container */}
                                <Box sx={{
                                    width: '100%',
                                    height: '300px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(6, 182, 212, 0.1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Chart Grid Lines */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        padding: '20px 40px'
                                    }}>
                                        {/* Horizontal Grid Lines */}
                                        {[0, 1, 2, 3, 4].map((i) => (
                                            <Box key={i} sx={{
                                                width: '100%',
                                                height: '1px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                position: 'relative'
                                            }}>
                                                {/* Y-axis labels */}
                                                <Typography sx={{
                                                    position: 'absolute',
                                                    left: '-35px',
                                                    top: '-8px',
                                                    fontSize: '10px',
                                                    color: '#666'
                                                }}>
                                                    {(567.5 - i * 7.5).toFixed(1)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* SVG Chart Line */}
                                    <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
                                        <defs>
                                            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 0.3 }} />
                                                <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 0 }} />
                                            </linearGradient>
                                        </defs>

                                        {/* Chart Path - Beta Spread Curve */}
                                        <path
                                            d="M 40,200 Q 200,180 350,190 T 820,210"
                                            stroke="#06b6d4"
                                            strokeWidth="2"
                                            fill="none"
                                        />

                                        {/* Area under curve */}
                                        <path
                                            d="M 40,200 Q 200,180 350,190 T 820,210 L 820,280 L 40,280 Z"
                                            fill="url(#chartGradient)"
                                        />

                                        {/* Sell Point Marker */}
                                        <g>
                                            <circle cx="190" cy="185" r="4" fill="#f44336" />
                                            <path d="M 190,175 L 195,185 L 190,195 L 185,185 Z" fill="#f44336" />
                                            <text x="200" y="180" fontSize="10" fill="#f44336">Sell</text>
                                        </g>

                                        {/* Buy Point Marker */}
                                        <g>
                                            <circle cx="520" cy="208" r="4" fill="#4caf50" />
                                            <path d="M 520,198 L 525,208 L 520,218 L 515,208 Z" fill="#4caf50" />
                                            <text x="530" y="212" fontSize="10" fill="#4caf50">Buy</text>
                                        </g>
                                    </svg>

                                    {/* X-axis Time Labels */}
                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: '5px',
                                        left: '40px',
                                        right: '40px',
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}>
                                        {['10:00', '10:10', '10:20', '10:30', '10:40'].map((time, i) => (
                                            <Typography key={i} sx={{ fontSize: '10px', color: '#666' }}>
                                                {time}
                                            </Typography>
                                        ))}
                                    </Box>

                                </Box>

                                {/* Legend - Outside the graph */}
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mt: 1,
                                    justifyContent: 'center'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 12, height: 2, backgroundColor: '#06b6d4' }} />
                                        <Typography sx={{ fontSize: '10px', color: '#bfc9db' }}>Beta Spread</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 12, height: 2, backgroundColor: '#666', borderStyle: 'dashed' }} />
                                        <Typography sx={{ fontSize: '10px', color: '#bfc9db' }}>Upper Bend</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 12, height: 2, backgroundColor: '#666', borderStyle: 'dashed' }} />
                                        <Typography sx={{ fontSize: '10px', color: '#bfc9db' }}>Lower Bend</Typography>
                                    </Box>
                                </Box>
                            </Box>

                                {/* COLUMN 2 - Info Section (Single Box with Strategy Parameters + Performance) */}
                                <Box sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                    border: '1px solid rgba(6, 182, 212, 0.2)',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                    {/* Inner Grid for 2 columns */}
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 3
                                    }}>
                                        {/* Strategy Parameters Section */}
                                        <Box>
                                            <Typography sx={{
                                                color: '#06b6d4',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                mb: 2,
                                                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                                                pb: 1
                                            }}>
                                                Strategy Parameters
                                            </Typography>
                                            {[
                                                { label: 'Benchmark Spread:', value: strategyDashboardData?.BenchmarkSpread || '0.0' },
                                                { label: 'BUY Threshold:', value: strategyDashboardData?.BuyThreshold || '0.0' },
                                                { label: 'SELL Threshold:', value: strategyDashboardData?.SellThreshold || '0.0' },
                                                { label: 'Grid Threshold:', value: strategyDashboardData?.GridThreshold || '0.0' },
                                                { label: 'Leg TPS:', value: strategyDashboardData?.LegTPS || '0.0' },
                                                { label: 'Accumulative TPS:', value: strategyDashboardData?.AccumulativeTPS || '0.0' },
                                                { label: 'Accumulative TP:', value: strategyDashboardData?.AccumulativeTP || '0.0' },
                                                { label: 'Accumulative SLS:', value: strategyDashboardData?.AccumulativeSLS || '0.0' }
                                            ].map((field, index) => (
                                                <Box key={index} sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    py: 0.75,
                                                    borderBottom: index < 7 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
                                                }}>
                                                    <Typography sx={{ fontSize: '13px', color: '#bfc9db' }}>
                                                        {field.label}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>
                                                        {field.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        {/* Performance Section */}
                                        <Box>
                                            <Typography sx={{
                                                color: '#4caf50',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                mb: 2,
                                                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                                                pb: 1
                                            }}>
                                                Performance
                                            </Typography>
                                            {[
                                                { label: 'Total Trades:', value: strategyDashboardData?.TotalTrades?.toString() || '0' },
                                                { label: 'Win Rate:', value: strategyDashboardData?.WinRate ? `${strategyDashboardData.WinRate}%` : '0%' },
                                                { label: 'Profit Factor:', value: strategyDashboardData?.ProfitFactor?.toString() || '0.0' },
                                                { label: 'Sharpe Ratio:', value: strategyDashboardData?.SharpeRatio?.toString() || '0.0' },
                                                { label: 'Max Drawdown:', value: strategyDashboardData?.MaxDrawdown ? `${strategyDashboardData.MaxDrawdown}%` : '0%' },
                                                { label: 'Total Profit:', value: strategyDashboardData?.TotalProfit ? `${strategyDashboardData.TotalProfit >= 0 ? '+' : ''}$${strategyDashboardData.TotalProfit.toFixed(2)}` : '$0.00' }
                                            ].map((field, index) => (
                                                <Box key={index} sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    py: 0.75,
                                                    borderBottom: index < 5 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
                                                }}>
                                                    <Typography sx={{ fontSize: '13px', color: '#bfc9db' }}>
                                                        {field.label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: '13px',
                                                        color: field.value.startsWith('+') ? '#4caf50' :
                                                               field.value.startsWith('-') ? '#f44336' : '#fff',
                                                        fontWeight: 600
                                                    }}>
                                                        {field.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default DashboardPage;